import React, {useEffect, useMemo, useState, useCallback} from "react";
import {Dropdown, Form, InputGroup} from "react-bootstrap";
import {LateralDrawer} from "./LateralDrawer";
import {EllipsisVerticalIcon} from "Components/Icons/EllipsisVerticalIcon";
import {PlusIcon} from "Components/Icons/PlusIcon";
import {MappingProperty, labelMap} from "Components/CourseCatalog/CurriculumMapping";
import {ArchiveIcon} from "Components/Icons/ArchiveIcon";
import {EditIcon} from "Components/Icons/EditIcon";
import {DeleteIcon} from "Components/Icons/DeleteIcon";
import {StyledModal} from "./StyledModal";
import {CustomOptionType} from "Stores/courseStore";
import {useIsMounted} from "@/hooks/useIsMounted";
import {observer} from "mobx-react";
import { MagnifyingIcon } from "Components/Icons/MagnifyingIcon";

type EditionMode = "delete" | "edit";

interface InputMultiSelectWCustomOptionsProps {
    property: MappingProperty;
    name: string;
    value: string[];
    disabled?: boolean;
    placeHolder?: string;
    options?: Array<CustomOptionType>;
    placeHolderAdd?: string;
    onCreate: (e: string) => void;
    onEdit: (e: CustomOptionType) => void;
    onDelete: (e: string) => void;
    onChangeStatus: (e: CustomOptionType, newStatus: "active" | "archived") => void;
    onSelect: (newValue: string[]) => void;
    isLoading: boolean;
    deleteError: boolean;
    closeDeleteError: () => void;
}

interface ICombinedOption {
    ruuid: string,
    name: string,
    groupLabel: string,
    status: string,
}

interface IGroupedOptions {
    label: string,
    items: ICombinedOption[]
}

const MenuOptionAction = ({ onChangeStatus, onEdit, onDelete, item, disabled }: {
    onChangeStatus: () => void,
    onEdit?: () => void,
    onDelete?: () => void,
    item: CustomOptionType,
    disabled: boolean;
}) => {
    return (
        <Dropdown className="dropdown-editoff">
            <Dropdown.Toggle id="dropdown-option-action" disabled={disabled}>
                <EllipsisVerticalIcon className={disabled ? "drop-ellipsis-icon drop-ellipsis-icon-disabled" : "drop-ellipsis-icon"} />
            </Dropdown.Toggle>
            <Dropdown.Menu>
                {item.status === "active" ? (
                    <Dropdown.Item onClick={onChangeStatus}>
                        Archive <ArchiveIcon />
                    </Dropdown.Item>
                ) : (
                    <Dropdown.Item onClick={onChangeStatus}>
                        Unarchive
                        <PlusIcon />
                    </Dropdown.Item>
                )}
                {onEdit && (
                    <Dropdown.Item onClick={onEdit}>
                        Edit
                        <EditIcon />
                    </Dropdown.Item>
                )}
                {onDelete && (
                    <Dropdown.Item onClick={onDelete}>
                        Delete <DeleteIcon />
                    </Dropdown.Item>
                )}
            </Dropdown.Menu>
        </Dropdown>
    );
};

export const InputMultiSelectWCustomOptions = observer(({ property, name, value, disabled, options, placeHolderAdd, onCreate, onDelete, onChangeStatus, onEdit, onSelect, isLoading, deleteError, closeDeleteError }: InputMultiSelectWCustomOptionsProps) => {
    const [openDrawer, setOpenDrawer] = useState(false);
    const [newLabel, setNewLabel] = useState("");
    const [editionMode, setEditionMode] = useState<EditionMode | undefined>(undefined);
    const [itemToEdit, setItemToEdit] = useState<CustomOptionType | undefined>(undefined);
    const [editableData, setEditableData] = useState<Array<CustomOptionType>>([]);
    const [blooms, setBlooms] = useState<Array<CustomOptionType>>([]);
    const [bloomsArchive, setBloomsArchive] = useState<Array<CustomOptionType>>([]);
    const [selectedValues, setSelectedValues] = useState<string[]>(value || []);
    const [searchTerm, setSearchTerm] = useState("");
    const isMounted = useIsMounted();

    useEffect(() => {
        setSelectedValues(value);
    }, []);

    const renderableData = useMemo(() => {
        function mergeObjects() {
            return editionMode === "edit" ?
                options?.filter((e) => e.ruuid !== itemToEdit?.ruuid).concat(editableData) :
                options?.concat(editableData);
        }

        return mergeObjects();
    }, [options, editableData, editionMode, itemToEdit]);

    useEffect(() => {
        if (isMounted()) {
            const blooms = options?.filter((e: CustomOptionType) => !e.custom_category && e.children?.find((c) => c.status !== "archived"));
            const bloomsArchive = options?.filter((e: CustomOptionType) => !e.custom_category && e.children?.find((c) => c.status === "archived"));

            setBlooms(blooms ?? []);
            setBloomsArchive(bloomsArchive ?? []);
        }
    }, [isMounted, options]);

    const generateUniqueId = () => {
        const now = new Date().getTime();
        // Use modulus to ensure the value doesn't exceed PostgreSQL's INTEGER max value
        const maxPostgresInt = 2147483647;
        return `tmp-${now % maxPostgresInt}`;
    };

    const onEditOption = (option: CustomOptionType) => {
        setItemToEdit(option);
        setEditionMode("edit");
        setNewLabel(option.name);
    };

    const labelName = property === "CLO" ? "Course Learning Outcome" :
        property === "CognitiveLevel" ? "Cognitive Level" :
        property === "TeachingMethod" ? "Teaching Method" : "Domain Competency";

    
    const flattenOptions = (options) => {
        return options.reduce((acc, option) => {
            if (option.children && option.children.length > 0) {
                const childOptions = option.children.map((child) => ({
                    ruuid: child.ruuid,
                    name: child.name,
                    status: option.status,
                    groupLabel: option.name,
                }));
                return [...acc, ...childOptions];
            }
            if (option.ruuid && option.name && option.status !== "archived") {
                acc.push({ ruuid: option.ruuid, name: option.name, status: option.status });
            }
            return acc;
        }, []);
    };

    const getSelectedNames = (selectedValues: string[], allOptions: ICombinedOption[]) => {
        return selectedValues
          .map((ruuid) => allOptions.find((opt) => opt.ruuid === ruuid)?.name)
          .filter(Boolean)
          .join(", ");
    };

    const combinedOptions = flattenOptions(options);

    const groupedOptions = useMemo<IGroupedOptions[]>(() => {
        // Group options by label
        const groups = {} as IGroupedOptions;
    
        combinedOptions.forEach((item: ICombinedOption) => {
          const groupLabel = item.groupLabel || "";
    
            if (item.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                groupLabel.toLowerCase().includes(searchTerm.toLowerCase())
            ) {
                if (!groups[groupLabel]) groups[groupLabel] = [];
                groups[groupLabel].push(item);
            }
        });
    
        return Object.entries(groups).map(([label, items]) => ({
          label,
          items,
        }));
    }, [combinedOptions, searchTerm]);

    const handleSelect = useCallback(
        (ruuid: string) => {
            setSelectedValues((prevValues: string[]) => {
                const alreadySelected = prevValues.includes(ruuid as string);
                const newValues = alreadySelected ? prevValues.filter((val) => val !== (ruuid as string)) : [...prevValues, ruuid as string];

                onSelect(newValues);
                return newValues;
            });
        },
        [onSelect]
    );

    const isOptionEmpty = !options?.filter((option) => (option.children && option.children.length > 0) || option.status !== "archived")?.length;

    return (
        <div className="select-custom-options">
            <Form.Group className="input-with-label input-mapping">
                <Dropdown autoClose="outside">
                    <Dropdown.Toggle
                        id="dropdown-basic"
                        disabled={disabled}
                        style={{
                            display: "inline-block",
                            width: "290px",
                            padding: "0 10px",
                            border: "1px solid #f2f2f2",
                            backgroundColor: "transparent",
                            color: "#212529",
                            height: "48px",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                        }}
                    >
                        {selectedValues.length > 0
                            ? getSelectedNames(selectedValues, combinedOptions)
                            : `Choose ${labelName}`}
                    </Dropdown.Toggle>
                    
                    <Dropdown.Menu>
                        {labelMap[property] === labelMap.CognitiveLevel ? (
                            <>
                                <div className="searchbox d-flex align-items-center">
                                    <Form.Control
                                        value={searchTerm}
                                        onChange={(e) => setSearchTerm(e.target.value)}
                                        type="text"
                                        as={"input"}
                                        placeholder="Search"
                                    />
                                    <MagnifyingIcon />
                                </div>

                                <Dropdown.Divider />
                            </>) : null}

                        <div className="teaching-method">
                                {/* Render Grouped and Filtered Options */}
                                {groupedOptions.map((group) => (
                                    <React.Fragment key={`group-for-option-${group.label ?? group.items[0].ruuid}`}>
                                        {group.label && <Dropdown.Header>{group.label}</Dropdown.Header>}
                                        {group.items.map((item) => (
                                            <Dropdown.Item
                                                key={item.name}
                                                as="div"
                                                role="option"
                                                className="item-option d-flex align-items-center"
                                                onClick={() => handleSelect(item.ruuid as string)}
                                                active={selectedValues.includes(item.ruuid as string)}
                                            >
                                                <Form.Check type="checkbox" id={`checkbox-${item.ruuid}`} checked={selectedValues.includes(item.ruuid as string)} onChange={() => handleSelect(item.ruuid as string)} className="me-2" />
                                                <span>{item.name}</span>
                                            </Dropdown.Item>
                                        ))}
                                    </React.Fragment>
                                ))}
                        </div>

                        {isOptionEmpty && (
                            <Dropdown.Item as="p" className="empty-method" disabled>
                                There is no {labelName} created, <br /> add a new one using the button below.
                            </Dropdown.Item>
                        )}

                        {/* Display message if no options match the search */}
                        {groupedOptions.length === 0 && (
                            <Dropdown.Item as="p" className="empty-method" disabled>No options found</Dropdown.Item>
                        )}

                        {/* <Dropdown.Divider /> */}
                        <Dropdown.Item as="div" role="button" className="action-button d-flex gap-1 align-items-center justify-content-center" onClick={() => setOpenDrawer(true)}>
                            {isOptionEmpty ? <PlusIcon className="action-button__icons" /> : <EditIcon className="action-button__icons mb-1" />}
                            <span>{`${isOptionEmpty ? "Add" : "Edit"} ${labelName}`}</span>
                        </Dropdown.Item>
                    </Dropdown.Menu>
                </Dropdown>
            </Form.Group>
            <LateralDrawer
                isOpen={openDrawer}
                onClose={() => setOpenDrawer(false)}
                title={labelName}
                confirmButton={{
                    title: "Save changes",
                    onClick: (e) => {
                        e?.preventDefault();
                        editableData.forEach((item: CustomOptionType) => {
                            onCreate(item.name);
                        });
                        setEditableData([]);
                        setOpenDrawer(false);
                    },
                    disabled: !editableData.length,
                }}
                cancelButton={{
                    title: "Cancel",
                    onClick: () => {
                        setOpenDrawer(false);
                        setEditableData([]);
                    },
                }}
                isLoading={isLoading}
            >
                <div className="drawer-form custom-options" data-testid="drawer-edit" id="input-w-custom">
                    <Form.Group className="input-with-label">
                        <Form.Label className="label-input" htmlFor={name}>
                            Add a new {labelName}
                        </Form.Label>
                        <InputGroup className="input-group-new-label">
                            <Form.Control
                                className="input-edit"
                                type="text"
                                value={newLabel}
                                onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                                    setNewLabel(e.target.value);
                                }}
                                name="course-title"
                                placeholder={placeHolderAdd}
                            />
                            <InputGroup.Text className={newLabel !== "" ? "addNewLabelPlusIcon" : "addNewLabelPlusIconDisabled"}>
                                <PlusIcon
                                    className={newLabel !== "" ? "addNewLabelPlusIcon" : "addNewLabelPlusIconDisabled"}
                                    onClick={() => {
                                        newLabel !== "" && editionMode === undefined
                                            ? (setEditableData(editableData.concat([{ name: newLabel, ruuid: generateUniqueId(), status: "active" }])), setNewLabel(""))
                                            : newLabel !== "" &&
                                              editionMode === "edit" &&
                                              (onEdit({
                                                  name: newLabel,
                                                  ruuid: itemToEdit?.ruuid,
                                                  status: itemToEdit?.status,
                                              }),
                                              setNewLabel(""),
                                              setEditionMode(undefined));
                                    }}
                                />
                            </InputGroup.Text>
                            <Form.Control.Feedback type="invalid">Please provide a new {labelName}.</Form.Control.Feedback>
                        </InputGroup>
                    </Form.Group>
                    {options && options?.length ? (
                        <React.Fragment>
                            {(Boolean(blooms.length) || Boolean(bloomsArchive.length)) && (
                                <div className="blooms-section">
                                    {blooms.map((option: CustomOptionType) => {
                                        const { name, children = [] } = option;
                                        const unarchivedChildren = children.filter((c) => c.status !== "archived");
                                        return (
                                            <React.Fragment key={`option-unarchive-${option.ruuid}`}>
                                                <div className="list-title" key={option.ruuid}>
                                                    {name}
                                                </div>
                                                {unarchivedChildren.map((childCategory) => (
                                                    <div className="options-display" key={childCategory.ruuid}>
                                                        {childCategory.name}
                                                        <MenuOptionAction
                                                            item={childCategory}
                                                            onChangeStatus={() => {
                                                                childCategory.status === "archived" ? onChangeStatus(childCategory, "active") : onChangeStatus(childCategory, "archived");
                                                            }}
                                                            disabled={childCategory.ruuid?.includes("tmp") ?? false}
                                                        />
                                                    </div>
                                                ))}
                                            </React.Fragment>
                                        );
                                    })}
                                </div>
                            )}
                            {renderableData
                                ?.filter((e: CustomOptionType) => e.custom_category === true && e.children && e.children?.length > 0)
                                .map((option: CustomOptionType) => {
                                    const { name, children = [] } = option;
                                    const unarchivedChildren = children.filter((c) => c.status !== "archived");
                                    return (
                                        <React.Fragment key={`unarchivedChildren-custom-${option.ruuid}`}>
                                            <div className="list-title">{name}</div>
                                            {unarchivedChildren.map((children) => (
                                                <div className="options-display" key={children.ruuid}>
                                                    {children.name}
                                                    <MenuOptionAction
                                                        item={children}
                                                        onDelete={() => {
                                                            setItemToEdit(children);
                                                            setEditionMode("delete");
                                                        }}
                                                        onChangeStatus={() => {
                                                            children.status === "archived" ? onChangeStatus(children, "active") : onChangeStatus(children, "archived");
                                                        }}
                                                        onEdit={() => onEditOption(children)}
                                                        disabled={children.ruuid?.includes("tmp") ?? false}
                                                    />
                                                </div>
                                            ))}
                                        </React.Fragment>
                                    );
                                })}
                            {renderableData
                                ?.filter((e: CustomOptionType) => e.status === "active")
                                .map((option: CustomOptionType) => {
                                    return (
                                        <div className="options-display" key={option.ruuid}>
                                            {option.name}
                                            <MenuOptionAction
                                                item={option}
                                                onDelete={() => {
                                                    setItemToEdit(option);
                                                    setEditionMode("delete");
                                                }}
                                                onChangeStatus={() => {
                                                    option.status === "archived" ? onChangeStatus(option, "active") : onChangeStatus(option, "archived");
                                                }}
                                                onEdit={() => onEditOption(option)}
                                                disabled={option.ruuid?.includes("tmp") ?? false}
                                            />
                                        </div>
                                    );
                                })}
                            <div className="list-title">Archived:</div>
                            {bloomsArchive.map((option) => {
                                const { name, children = [] } = option;
                                const archivedChildren = children.filter((c) => c.status === "archived");
                                return (
                                    <React.Fragment key={`boolms-archive-${option.ruuid}`}>
                                        <div className="list-title">{name}</div>
                                        {archivedChildren.map((childCategory) => (
                                            <div className="options-display" key={childCategory.ruuid}>
                                                <div className="options-display-label">{childCategory.name}</div>
                                                <MenuOptionAction
                                                    item={childCategory}
                                                    onChangeStatus={() => {
                                                        childCategory.status === "archived" ? onChangeStatus(childCategory, "active") : onChangeStatus(childCategory, "archived");
                                                    }}
                                                    disabled={childCategory.ruuid?.includes("tmp") ?? false}
                                                />
                                            </div>
                                        ))}
                                    </React.Fragment>
                                );
                            })}
                            {renderableData
                                ?.filter((e) => e.status === "archived" && e.editable !== false)
                                .map((option) => {
                                    return (
                                        <div className="options-display" key={option.ruuid}>
                                            <div className="options-display-label">{option.name}</div>
                                            <MenuOptionAction
                                                item={option}
                                                onDelete={() => {
                                                    setItemToEdit(option);
                                                    setEditionMode("delete");
                                                }}
                                                onChangeStatus={() => {
                                                    option.status === "archived" ? onChangeStatus(option, "active") : onChangeStatus(option, "archived");
                                                }}
                                                onEdit={() => onEditOption(option)}
                                                disabled={option.ruuid?.includes("tmp") ?? false}
                                            />
                                        </div>
                                    );
                                })}
                            {renderableData
                                ?.filter((e: CustomOptionType) => e.custom_category === true && e.children && e.children?.find((c) => c.status === "archived"))
                                .map((option: CustomOptionType) => {
                                    const { name, children = [] } = option;
                                    const archivedChildren = children.filter((c) => c.status === "archived");
                                    return (
                                        <React.Fragment key={`renderableData-custom-${option.ruuid}`}>
                                            <div className="list-title">{name}</div>
                                            {archivedChildren.map((childCategory) => (
                                                <div className="options-display" key={childCategory.ruuid}>
                                                    <div className="options-display-label">{childCategory.name}</div>
                                                    <MenuOptionAction
                                                        item={childCategory}
                                                        onChangeStatus={() => {
                                                            childCategory.status === "archived" ? onChangeStatus(childCategory, "active") : onChangeStatus(childCategory, "archived");
                                                        }}
                                                        disabled={childCategory.ruuid?.includes("tmp") ?? false}
                                                    />
                                                </div>
                                            ))}
                                        </React.Fragment>
                                    );
                                })}
                        </React.Fragment>
                    ) : (
                        <div className="no-data-message">There is no {labelName}s.</div>
                    )}
                </div>
            </LateralDrawer>
            <StyledModal
                isOpen={editionMode === "delete"}
                onClose={() => {
                    setItemToEdit(undefined);
                    setEditionMode(undefined);
                    setSelectedValues([]);
                }}
                title={"Are you sure you want to delete Mapping?"}
                subtitle={"This action cannot be undone"}
                cancelButtonLabel="Cancel"
                confirmButtonLabel={"Delete"}
                onSubmit={(e) => {
                    e.preventDefault();
                    onDelete(itemToEdit?.ruuid ?? "");
                    setItemToEdit(undefined);
                    setEditionMode(undefined);
                    setSelectedValues([]);
                }}
            />
            <StyledModal
                isOpen={deleteError}
                onClose={() => closeDeleteError()}
                title={"An error has ocurred"}
                subtitle={`Unable to delete this ${labelName} because it is currently mapped to courses. Please remove these mappings first.`}
                // cancelButtonLabel="Cancel"
                confirmButtonLabel={"Got it"}
                onSubmit={(e) => {
                    e.preventDefault(), setItemToEdit(undefined);
                    setEditionMode(undefined);
                    closeDeleteError();
                }}
            />
        </div>
    );
});
